import React from 'react';
import { useTranslation } from 'react-i18next';

const AboutUs = () => {
  const { t } = useTranslation();

  // Data for reusable sections
  const coreValues = [
    t('aboutUs.coreValues.excellence'),
    t('aboutUs.coreValues.enviableReputation'),
    t('aboutUs.coreValues.speedAndTimely'),
    t('aboutUs.coreValues.efficientReliable'),
    t('aboutUs.coreValues.customerSatisfaction'),
    t('aboutUs.coreValues.globalPresence')
  ];

  const sourcingBenefits = [
    {
      title: "Time-Saving Efficiency:",
      content: "One of the main advantages of construction material sourcing is the time contractors save. Rather than manually reaching out to suppliers, making endless calls, or visiting warehouses, these services do all the heavy lifting. For the busy contractor, this means more time focusing on the actual build. We handle the materials while you focus on the execution of the project."
    },
    {
      title: "Best Deals and Quality Assurance:",
      content: "Building material sourcing services ensure not only the best prices but also quality. These services have established networks and know which suppliers provide the best materials at competitive prices. Contractors can rest assured they're getting top-tier products without breaking their budget."
    },
    {
      title: "Streamlined Equipment Rentals:",
      content: "Need a specific piece of machinery for a short-term project? Rental equipment sourcing becomes invaluable. These services can pinpoint the exact equipment required, source it from reliable renters, and often help in coordinating timely deliveries."
    },
    {
      title: "Financial Benefits:",
      content: "Engaging with a material sourcing service might seem like an added expense, but in reality, it's an investment. Contractors often find that the savings from bulk deals, trade discounts, and optimal rental terms surpass the service fees."
    }
  ];

  const whyChooseUs = [
    "UAE based but with a Global reach",
    "Affordable, get value for money",
    "No Contracts & No Set Up Fees, just register with us",
    "We Never Charge For Quotes, it is our responsibility",
    "Track your order with our dedicated client update consultants",
    "We are built and thrive on Transparency",
    "We are Customer Centric",
    "Globally connected and can ship from any port(sea and air) – 360-degree logistics"
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
      {/* Hero Section */}
      <section className="mb-6 sm:mb-8 lg:mb-10">
        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#063970] mb-4 sm:mb-6 leading-tight">{t('aboutUs.title')}</h1>

        <div className="bg-blue-50 p-4 sm:p-6 lg:p-8 rounded-lg">
          <p className="text-base sm:text-lg lg:text-xl text-[#000] mb-3 sm:mb-4 leading-relaxed">
            {t('aboutUs.description1')}
          </p>
          <p className="text-base sm:text-lg lg:text-xl text-[#000] leading-relaxed">
            {t('aboutUs.description2')}
          </p>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="mb-6 sm:mb-8 lg:mb-10">
        <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold text-[#063970] mb-4 sm:mb-6 leading-tight">{t('aboutUs.weAreKnownFor')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
          {coreValues.map((value, index) => (
            <div key={index} className="bg-white p-3 sm:p-4 lg:p-6 rounded-lg shadow-md border-l-4 border-blue-500">
              <p className="text-sm sm:text-base lg:text-lg font-medium text-[#063970] leading-relaxed">{value}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Services Section */}
      <section className="mb-6 sm:mb-8 lg:mb-10">
        {/* <h2 className="text-3xl font-semibold text-[#063970] mb-2">Our Solutions and Services</h2> */}

        <div className="mb-6 sm:mb-8">
          <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-medium text-[#063970] mb-4 sm:mb-6 leading-tight">Building and Construction Materials Solutions</h3>
          <p className="text-sm sm:text-base lg:text-lg text-[#000] mb-4 leading-relaxed">
            We prescribe, and supply building and construction materials for all phases and application in the built environment. Our comprises materials from conception to furnishing, working closely with architects, consultants, owners, investors, designers all the key stakeholders and professionals involved in conceiving, designing and execution of projects. No project is too big or small for us. Every project is unique for us and give it the required attention.
          </p>
          <p className="text-sm sm:text-base lg:text-lg text-[#000] leading-relaxed">
            We supply materials for projects and local distributors. Check our product list for your most trusted materials and solutions.
          </p>
        </div>

        <div className="mb-6 sm:mb-8">
          <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-medium text-[#063970] mb-4 sm:mb-6 leading-tight">Construction Services</h3>
          <ul className="list-disc pl-4 sm:pl-6 space-y-2 text-sm sm:text-base lg:text-lg text-[#000]">
            <li>Product and Project Development</li>
            <li>Procurement and Sourcing</li>
            <li>Project Management</li>
            <li>Material Prescription and Specification</li>
            <li>Installation</li>
          </ul>
        </div>

        <div className="mb-6 sm:mb-8">
          <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-medium text-[#063970] mb-4 sm:mb-6 leading-tight">Turnkey Projects</h3>
          <p className="text-sm sm:text-base lg:text-lg text-[#000] mb-4 leading-relaxed">
            We have the expertise in hand developing and executing turnkey projects from conception to hand over. Our team is diverse with all the required skills, technology and experience in handling any scope of projects. From designing, engineering, procurement, execution to the satisfaction of our clients. We shoulder all the work giving our clients the peace of mind to concentrate on core businesses.
          </p>
          <p className="text-sm sm:text-base lg:text-lg text-[#000] leading-relaxed">
            Turning ideas to viable and profitable business is the most important deliverable of a project, and this the perspective and culture at Omanye.
          </p>
        </div>
      </section>

      {/* Sourcing Services Section */}
      <section className="mb-6 sm:mb-8 lg:mb-10 bg-gray-50 p-4 sm:p-6 lg:p-8 rounded-lg">
        <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold text-[#063970] mb-4 sm:mb-6 lg:mb-8 leading-tight">Material Sourcing and Procurement Service</h2>

        <div className="mb-6 sm:mb-8">
          <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-medium text-[#063970] mb-4 sm:mb-6 leading-tight">Hassle-Free Procurement Services</h3>
          <p className="text-sm sm:text-base lg:text-lg text-[#000] mb-4 leading-relaxed">
            We transform the intricate task of material sourcing into a streamlined experience. Skip the endless calls and complex negotiations. We vet requirements and align them to our available portfolio of suppliers, negotiate deals, and ensure timely deliveries for you. This is made possible because we handle all the supply and delivery. With our expertise, your procurement process is efficient, reliable, and effortless. Choose simplicity; choose Omanye.
          </p>
          <ul className="list-disc pl-4 sm:pl-6 space-y-2 text-sm sm:text-base lg:text-lg text-[#000]">
            <li>We handle the legwork; you reap the benefits.</li>
            <li>Direct comparisons from nearby suppliers.</li>
            <li>Competitive rates with optimal delivery timelines.</li>
            <li>Instant insights on inventory and restock dates.</li>
            <li>Comprehensive PDF with essential supplier contacts.</li>
          </ul>
        </div>

        <div className="mb-6 sm:mb-8">
          <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-medium text-[#063970] mb-4 sm:mb-6 leading-tight">Material Price Shopping and Comparison</h3>
          <p className="text-sm sm:text-base lg:text-lg text-[#000] mb-4 leading-relaxed">
            We actively compare prices across a wide range of suppliers, to ensure that you are getting the most value for your money. We use our partnership to get you the best and most competitive rates.
          </p>
          <ul className="list-disc pl-4 sm:pl-6 space-y-2 text-sm sm:text-base lg:text-lg text-[#000]">
            <li>Maximized Savings</li>
            <li>Informed Decision Making</li>
            <li>Negotiation Leverage</li>
            <li>Timely Rental Coordination</li>
          </ul>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="mb-6 sm:mb-8 lg:mb-10">
        <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold text-[#063970] mb-4 sm:mb-6 leading-tight">The Benefits of Material Sourcing Services</h2>
        <p className="text-sm sm:text-base lg:text-lg text-[#000] mb-6 sm:mb-8 leading-relaxed">
          In the fast-paced world of construction, contractors and developers and some cases investors, are often swamped with tasks that go beyond their primary job – building. Between managing crews, overseeing project progress, and ensuring quality, finding the time to hunt for the best deals on materials and equipment can be a challenge. Enter the boon of material sourcing services. Omanye provides the safe-haven of one-stop-basket with unlimited options of solutions for all your needs.
        </p>

        <div className="space-y-4 sm:space-y-6">
          {sourcingBenefits.map((item, index) => (
            <div key={index} className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg shadow-sm">
              <h3 className="text-base sm:text-lg md:text-xl lg:text-2xl font-semibold text-[#063970] mb-3 sm:mb-4 leading-tight">{item.title}</h3>
              <p className="text-sm sm:text-base lg:text-lg text-[#000] leading-relaxed">{item.content}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="mb-6 sm:mb-8 lg:mb-10 bg-[#063970] text-white p-4 sm:p-6 lg:p-8 rounded-lg">
        <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold mb-4 sm:mb-6 leading-tight">Why Choose Omanye?</h2>
        <p className="text-sm sm:text-base lg:text-lg mb-6 sm:mb-8 leading-relaxed">
          Hire Omanye to do your tedious takeoff work. You will have more time and save more money when you use our services. Focus on what's really important and leave this part to us. Our team of project managers, material specification specialists, and engineers are ready to serve you!
        </p>

        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
          {whyChooseUs.map((item, index) => (
            <li key={index} className="flex items-start">
              <svg className="h-4 w-4 sm:h-5 sm:w-5 text-blue-200 mt-1 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-blue-50 text-sm sm:text-base lg:text-lg leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Contact Section */}
      <section className="bg-gray-100 p-4 sm:p-6 lg:p-8 rounded-lg">
        <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold text-[#063970] mb-4 sm:mb-6 leading-tight">Contact Us</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          <div>
            <h3 className="text-lg sm:text-xl font-medium text-[#000] mb-3 sm:mb-4">Address</h3>
            <p className="text-sm sm:text-base lg:text-lg text-gray-600 leading-relaxed">FOB 51927 Compass Building, Al Shohoda Road, Al Hamra Industrial Zone-FZ, Ras Al Khaimah, UAE</p>

            <h3 className="text-lg sm:text-xl font-medium text-[#000] mt-4 sm:mt-6 mb-3 sm:mb-4">Contact Information</h3>
            <p className="text-sm sm:text-base lg:text-lg text-gray-600">Tel: +971503118846</p>
            <p className="text-sm sm:text-base lg:text-lg text-gray-600">WhatsApp: +971503118846</p>

          </div>

          <div>
            {/* <h3 className="text-xl font-medium text-[#000] mb-3">Social Media</h3>
            <div className="flex space-x-4">
              <a href="#" className="text-blue-600 hover:text-blue-800">Facebook</a>
              <a href="#" className="text-blue-600 hover:text-blue-800">LinkedIn</a>
              <a href="#" className="text-blue-600 hover:text-blue-800">X</a>
              <a href="#" className="text-blue-600 hover:text-blue-800">YouTube</a>
              <a href="#" className="text-blue-600 hover:text-blue-800">Instagram</a>
            </div> */}

            <h3 className="text-lg sm:text-xl font-medium text-[#000] mt-3 sm:mt-4 mb-3 sm:mb-4">Professionals and Consultants Desk</h3>
            <ul className="list-disc pl-4 sm:pl-6 space-y-2 text-sm sm:text-base lg:text-lg text-gray-600">
              <li>Designer and Interior</li>
              <li>Architects</li>
              <li>Consultant</li>
              <li>Installer</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;